import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Link, useParams } from 'react-router-dom';
import { isEmpty } from 'underscore';
import ReactRouterPropTypes from 'react-router-prop-types';

import { ThresholdsPopup } from "./DetailPageComponents/ThresholdsPopup.jsx";

import { ClassifierPicker } from "./ClassifierPicker.jsx";
import { READ, WRITE } from './Om7Sense';

import { openReport } from "./ReportingPage.jsx";
import { TranslationContext } from "./hooks/useTranslationContext";
import { Om7Sense as p } from './Om7Sense';
import useSWRSubscription from "swr/subscription";
import { ApiConnectionContext } from "./hooks/useApiConnectionContext";
import axios from "axios";
import { useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
import { SearchableTable } from "./util/tables/SearchableTable";
import { Pagination } from "./util/tables/Pagination";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";


const DURATION_ERROR_SHOW = 5000;
const DELETE_KEY_LOADING = "__LOADING__";

export const RulesPage = () => {
    const t = useContext(TranslationContext);
    const { loggedInAs } = useContext(ApiConnectionContext);
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const [ descriptions, setDescriptions ] = useState({});
    const readOnly = !useMemo(() => p.checkPermission(WRITE, "thr"), [ loggedInAs ]);
    const canRead = useMemo(() => p.checkPermission(READ, "thr"), [ loggedInAs ]);
    const [ loadingDescriptions, setLoadingDescriptions ] = useState(false);
    const [ descriptionsError, setDescriptionsError ] = useState();
    const [ isDeleting, setIsDeleting ] = useState(undefined);


    if (!canRead) {
        return (
            <div className="empty">
                <div className="empty-icon">
                    <i className="icon icon-people"></i>
                </div>
                <p className="empty-title h5">{ t("thresholds.no_permission") }</p>
            </div>
        );
    }

    const transformThr = () => {
        let rules = {};
        const rulesTopic = p.DS.thr;

        function expandRule(topic, currentPathParts) {
            if (topic.ts && topic.name !== undefined) {
                rules[currentPathParts.join("/")] = topic;
            } else {
                for (let topicName in topic) {
                    if (topicName === "ts" || topicName === "v" || topicName === "new") {
                        continue;
                    }
                    const subTopic = topic[topicName];
                    if (!subTopic) {
                        continue;
                    }
                    currentPathParts.push(topicName);
                    expandRule(subTopic, currentPathParts);
                    currentPathParts.pop();
                }
            }
        }

        // Iterate over all subtopics until we find a leaf with "name" and "ts" attrs
        if (rulesTopic) {
            expandRule(rulesTopic, []);
        }

        // Now we have a dict mapping each rule with its path as key in rules.
        return rules;
    }

    const { data: rules, error: rulesError } = useSWRSubscription(["thr/*"], ([_], { next }) => {
        let subscription;
        p.subscribe("thr/*", () => {
            next(null, transformThr());
        }, true).then((sub) => {
            subscription = sub;
        }, (error) => {
            next(error);
        });
        return () => {
            subscription?.();
        };
    });

    const rulesLackingDescriptions = useMemo(() => {
        let rulesThatLackDescriptions = [];

        // Check which descriptions are missing
        for (const rule in rules) {
            if (!descriptions[rule]) {
                rulesThatLackDescriptions.push(rule);
            }
        }

        return rulesThatLackDescriptions;

    }, [rules, descriptions]);

    useEffect(() => {
        if (!isEmpty(rulesLackingDescriptions) && !loadingDescriptions) {
            setLoadingDescriptions(true);
            axios.post('/apiv2/classifier/describe', {
                paths: Object.keys(rules),
            }).then((newDescriptions) => {
                if (newDescriptions) {
                    setDescriptions((descriptions) => {
                        return Object.assign({}, descriptions, newDescriptions.data);
                    })
                }
            }).catch((e) => {
                setDescriptionsError(e);
            }).finally(() => {
                setLoadingDescriptions(false);
            })
        }
    }, [
        rulesLackingDescriptions,
        loadingDescriptions,
        setLoadingDescriptions,
        setDescriptions,
        setDescriptionsError,
    ]);

    const deleteRule = useCallback((topic, name) => {
        setIsDeleting((deleting) => {
            if (!deleting || deleting !== topic) {
                return topic;
            } else {
                p.write("thr/" + topic, { delete: true }).then(() => {
                    setIsDeleting(undefined);
                }, (error) => {
                    console.error('Cannot delete rule', error);
                    setDescriptionsError(t("thresholds.error_couldnt_delete__", { name }));
                    window.setTimeout(() => {
                        setDescriptionsError(undefined);
                    }, DURATION_ERROR_SHOW);
                    return undefined;
                });
                return DELETE_KEY_LOADING;
            }
        });

    }, [setIsDeleting, setDescriptionsError]);

    const saveRule = useCallback((rule, name) => {
        console.log("Creating rule", rule, name);
        p.write("thr/" + rule, { name, wad: 0, wbd: 0, cad: 0, cbd: 0, store: true }).then(() => {
            // Success
            navigate("/rules/" + rule);
        }, (err) => {
            // Error state
            console.error("Cannot save rule", err);
            setDescriptionsError(t("general.error_occured_code", { error: err.toString() }));
            window.setTimeout(() => {
                setDescriptionsError(undefined);
            }, DURATION_ERROR_SHOW);
        });
    }, [setDescriptionsError, navigate]);

    // Iterate over it and render
    const data = useMemo(() => {
        let data = [];
        for (let topic in rules) {
            const rule = rules[topic];
            const description = descriptions[topic];
            if (!description) {
                continue;
            }

            data.push({ topic, rule, description });
        }
        return data;
    }, [descriptions, rules]);

    const columns = useMemo(() => {
        function renderThr(rule, key, unit) {
            let icon = "icon ";
            let className = "";
            switch (key.substring(0, 2)) {
                case "wa":
                    icon = "⤉";
                    break;
                case "ca":
                    icon = "⇞";
                    break;
                case "wb":
                    icon = "⤈";
                    break;
                case "cb":
                    icon = "⇟";
                    break;
            }

            const isCritical = key.charAt(0) === 'c';
            let val = rule[key];

            if (val === null || val === undefined) {
                // It's enabled
                className = "text-gray";
                val = rule[key + "d"];

                if (val === null || val === undefined) {
                    val = "-";
                }
            }

            return (
                <span className={ className }>
                { icon }
                    { val } { unit }
            </span>
            );
        }

        return [
            {
                Header: t("general.name"),
                accessor: 'rule.name',
            }, {
            Header: t("thresholds.rule_details"),
            Cell: ({ row: { original: { rule, description: { translation, unit } } } }) => (
                <>
                    { translation }<br/>
                    { renderThr(rule, "ca", unit) }&nbsp;
                    { renderThr(rule, "wa", unit) }&nbsp;
                    { renderThr(rule, "wb", unit) }&nbsp;
                    { renderThr(rule, "cb", unit) }
                </>
            )
        }, {
            Header: t("thresholds.rule_actions"),
            Cell: ({ row: { original: { topic, rule: { name } } } }) => (
                <>
                    <button className="btn btn-primary" onClick={ () => openReport(topic, 'thisMonth', true) }>
                        { t("reporting.generate") }
                    </button>
                    &nbsp;
                    <Link to={ "rules/" + topic } className="btn btn-primary">
                        { t("general.edit") }
                    </Link>&nbsp;
                    <button className={ "btn " + (isDeleting === topic ? "btn-error" : "btn-primary") }
                            onClick={ () => deleteRule(topic, name) }>
                        { t("general.delete") }
                    </button>
                </>
            ),
        }
        ]
    }, [ t, isDeleting ]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page

        // The rest of these things are super handy, too ;)
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state,
        visibleColumns,
        setGlobalFilter,
    } = useTable({
        columns,
        data,
        initialState: {
            pageSize: 20,
            sortBy: [{ id: 'name', desc: true }],
        }
    }, useGlobalFilter, useSortBy, usePagination);

    const isCreatingNew = params[0] === '/new';

    if (rules === undefined || loadingDescriptions || isDeleting === DELETE_KEY_LOADING) {
        return <div className="loader-element">
            <div className="loading loading-lg"></div>
        </div>;
    }

    let toast = null;
    if (rulesError || descriptionsError) {
        toast = (
            <div className="toast toast-error">
                { rulesError?.toString() || descriptionsError?.toString() }
            </div>
        );
    }

    if (isCreatingNew) {
        return (
            <>
                { toast }
                <ClassifierPicker onComplete={ saveRule } oneNewItemType={ t("thresholds.one_new_item_type") }
                                  thisItemType={ t("thresholds.this_item_type") }
                                  saveButtonText={ t("thresholds.create_rule_and_set_thresholds") }/>
            </>
        );
    }


    let popup = null;
    if (params[0] && params[0] !== "/new") {
        popup = <ThresholdsPopup thrPath={ params[0] } location={ location }
                                 onClose={ () => navigate("/rules") } navigate={navigate} />
    }

    return (
        <React.Fragment>
            <div className="sidebar">
                <div className="sidebar__sorting active">
                    <div className="siderbar__sorting__container">
                        { !readOnly && (
                            <Link className="btn btn-primary" to="/rules/new">
                                { t('thresholds.button_create_new_rule_wizard') }
                            </Link>
                        ) }
                    </div>
                </div>
            </div>

            { data.length === 0 ? (
                <div className="empty">
                    <div className="empty-icon">
                        <i className="icon icon-people"></i>
                    </div>
                    <p className="empty-title h5">
                        { t("thresholds.no_rules") }
                    </p>
                    { !readOnly && (
                        <div className="empty-action">
                            <Link className="btn btn-primary" to="/rules/new">
                                { t('thresholds.button_create_new_rule_wizard') }
                            </Link>
                        </div>
                    ) }
                </div>
            ) : (
                <div className="content overview reports">
                    <SearchableTable tableProps={ getTableProps() } globalFilter={ state.globalFilter }
                                     setGlobalFilter={ setGlobalFilter }
                                     state={ state } columns={ columns } visibleColumns={ visibleColumns }
                                     headerGroups={ headerGroups }
                                     prepareRow={ prepareRow } getTableBodyProps={ getTableBodyProps } page={ page }/>
                    <Pagination page={ page } pageCount={ pageCount } pageOptions={ pageOptions }
                                canPreviousPage={ canPreviousPage } canNextPage={ canNextPage } gotoPage={ gotoPage }
                                previousPage={ previousPage } nextPage={ nextPage } setPageSize={ setPageSize }
                                pageSize={ state.pageSize } pageIndex={ state.pageIndex }/>
                </div>
            ) }


            <div className="overview__footer">
                <a href="#">{ t('general.return_to_top') }</a>
            </div>

            { toast }
            { popup }
        </React.Fragment>
    );

}
